import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Users, UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { useAuth, useUsers } from "@/context/AuthContext";
import { initials } from "@/lib/format";
import { Card } from "@/components/settings/Card";

const COLORS = ["#3B82F6", "#A855F7", "#F59E0B", "#EC4899", "#14B8A6", "#EF4444", "#6366F1"];
const EMPTY = { name: "", email: "", password: "", role: "agent", color: COLORS[0] };

export const TeamSettings = () => {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const { data: users = [] } = useUsers();
  const [f, setF] = useState(EMPTY);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/users", f); setF(EMPTY); qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Membro aggiunto"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const patch = async (id, body) => { await api.patch(`/users/${id}`, body); qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Ruolo aggiornato"); };
  const remove = async (id) => {
    try { await api.delete(`/users/${id}`); qc.invalidateQueries({ queryKey: ["users"] }); toast.success("Membro rimosso"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Card testid="team-card" title="Membri del team" desc="Agenti e amministratori con accesso alla casella condivisa" icon={Users}>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-6 border-b border-slate-100">
        <Input data-testid="team-name-input" required placeholder="Nome e cognome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <Input data-testid="team-email-input" required type="email" placeholder="email@azienda.it" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <Input data-testid="team-password-input" required type="password" minLength={6} placeholder="Password iniziale" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
          <SelectTrigger data-testid="team-role-select"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="agent">Agente</SelectItem><SelectItem value="admin">Amministratore</SelectItem></SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          {COLORS.map((c) => <button type="button" key={c} onClick={() => setF({ ...f, color: c })} className={`w-6 h-6 rounded-full transition-transform ${f.color === c ? "ring-2 ring-offset-2 ring-slate-900 scale-110" : ""}`} style={{ background: c }} />)}
        </div>
        <div className="flex justify-end"><Button data-testid="team-add-button" type="submit" className="bg-[#0F172A] hover:bg-[#1E293B]"><UserPlus size={15} className="mr-1.5" />Aggiungi membro</Button></div>
      </form>
      <ul className="divide-y divide-slate-100 -mx-6">
        {users.map((u) => (
          <li key={u.id} data-testid={`team-member-${u.email}`} className="px-6 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full grid place-items-center text-xs font-bold text-white" style={{ background: u.color }}>{initials(u.name)}</div>
            <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-slate-800">{u.name}{u.id === me.id && <span className="text-xs text-slate-400"> (tu)</span>}</div><div className="text-xs text-slate-500 truncate">{u.email}</div></div>
            <Select value={u.role} onValueChange={(v) => patch(u.id, { role: v })} disabled={u.id === me.id}>
              <SelectTrigger className="w-32 sm:w-40 h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="agent">Agente</SelectItem><SelectItem value="admin">Amministratore</SelectItem></SelectContent>
            </Select>
            <Button data-testid={`team-delete-${u.email}`} variant="ghost" size="icon" disabled={u.id === me.id} onClick={() => remove(u.id)}><Trash2 size={15} className="text-red-500" /></Button>
          </li>
        ))}
      </ul>
    </Card>
  );
};
